import { LogLevel, type Configuration, type RedirectRequest } from '@azure/msal-browser';

/**
 * MSAL configuration for the SPA.
 *
 * Single-tenant: the authority names the organisation's directory, so personal Microsoft accounts
 * cannot sign in even if someone tries.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_SPA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_TENANT_ID}`,

    // The app's own origin, because sign-in is a redirect: Entra sends this same tab back here, and
    // handleRedirectPromise() in main.tsx consumes the response before the first render. The request
    // that produced it is in this tab's sessionStorage, so it is found.
    //
    // Popups use popupRedirectUri below instead. Both must be registered on the app registration.
    redirectUri: window.location.origin,

    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // sessionStorage rather than localStorage: the tab closing ends the session. These are shared
    // clinic and back-office machines, and a token surviving in localStorage after someone walks
    // away is a real risk, not a theoretical one.
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(message);
        else if (level === LogLevel.Warning) console.warn(message);
      },
    },
  },
};

/** Scope for this app's own API. Graph scopes are requested by the API, not here. */
export const apiScopes: string[] = [import.meta.env.VITE_API_SCOPE];

/**
 * Redirect URI for the popup calls — mid-task consent and silent-refresh fallback.
 *
 * A blank page, deliberately not the app's origin. A popup navigates to this URI on the way back
 * from Entra, and the opener reads the response out of it across the window boundary. Pointing it at
 * the origin serves the SPA a second time inside the popup, whose handleRedirectPromise() then treats
 * a popup response as a redirect response and throws no_token_request_cache_error.
 * public/auth-redirect.html carries no script for exactly this reason.
 */
export const popupRedirectUri = `${window.location.origin}/auth-redirect.html`;

/**
 * Sign-in request. A redirect rather than a popup: popups depend on the opener being able to read
 * the popup's URL, which popup blockers, COOP headers and storage partitioning all break in ways
 * that surface as a window that opens and then does nothing. A redirect has no second window.
 */
export const loginRequest: RedirectRequest = {
  scopes: apiScopes,
  // Ensures the account picker appears rather than silently reusing whichever account the browser
  // last used — RDs and clinic staff share machines.
  prompt: 'select_account',
};

/**
 * True when the build has real values. A misconfigured deployment should say so on screen rather
 * than fail inside MSAL with an opaque error.
 */
export function isAuthConfigured(): boolean {
  const placeholder = '00000000-0000-0000-0000-000000000000';

  return Boolean(
    import.meta.env.VITE_SPA_CLIENT_ID &&
      import.meta.env.VITE_TENANT_ID &&
      import.meta.env.VITE_API_SCOPE &&
      import.meta.env.VITE_SPA_CLIENT_ID !== placeholder &&
      import.meta.env.VITE_TENANT_ID !== placeholder,
  );
}
